// An agent holds N labelled bearers so operators can tell which consumer holds
// what. Minting is additive and delete soft-revokes one bearer at a time.

import { Check, Copy, Loader2, Plus, Trash2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  type A2ABearerRecord,
  createAgentBearer,
  listAgentBearers,
  revokeAgentBearer,
} from '@/services/agents/config';

interface BearersPanelProps {
  orgId: string;
  agentId: string;
  /** Provided so the parent can disable the actions when locked / non-owner. */
  canMutate: boolean;
}

/** Tiny relative-time helper -- "10m ago", "2h ago", "3d ago". */
function relTime(iso: string | null): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.max(0, (Date.now() - ts) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export const BearersPanel: FC<BearersPanelProps> = ({
  orgId,
  agentId,
  canMutate,
}) => {
  const [bearers, setBearers] = useState<A2ABearerRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // New-bearer flow state
  const [newLabel, setNewLabel] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);
  const [revealedBearer, setRevealedBearer] = useState<string | null>(null);
  const [revealedLabel, setRevealedLabel] = useState<string>('');
  const [justCopied, setJustCopied] = useState<boolean>(false);

  // Per-row delete-in-flight set (bearer_ids)
  const [revoking, setRevoking] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAgentBearers(orgId, agentId);
      setBearers(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load bearers');
    } finally {
      setLoading(false);
    }
  }, [orgId, agentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      // The async Clipboard API only resolves inside a live user-activation
      // window; the auto-copy on mint fires after an await, so it often
      // rejects. Await it, and fall back to a hidden-textarea execCommand
      // (which works without the async gesture gap). Only claim success when
      // a copy actually landed — otherwise the UI would lie about it.
      let ok = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
          await navigator.clipboard.writeText(text);
          ok = true;
        }
      } catch {
        ok = false;
      }
      if (!ok && typeof document !== 'undefined') {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          ok = document.execCommand('copy');
          document.body.removeChild(ta);
        } catch {
          ok = false;
        }
      }
      if (ok) {
        setJustCopied(true);
        window.setTimeout(() => setJustCopied(false), 2000);
      }
      return ok;
    },
    [],
  );

  const handleCreate = useCallback(async () => {
    if (!canMutate || creating) return;
    setCreating(true);
    setError(null);
    try {
      const result = await createAgentBearer(orgId, agentId, newLabel.trim());
      if (!result || !result.a2a_bearer_token) {
        throw new Error('Server did not return a new bearer');
      }
      setRevealedBearer(result.a2a_bearer_token);
      setRevealedLabel(result.bearer.label || newLabel.trim());
      // Best-effort auto-copy. If it fails (past the user-activation
      // window), justCopied stays false and the manual "Copy bearer"
      // button — a fresh gesture — is the reliable path.
      void copyToClipboard(result.a2a_bearer_token);
      setNewLabel('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create bearer');
    } finally {
      setCreating(false);
    }
  }, [canMutate, creating, orgId, agentId, newLabel, refresh, copyToClipboard]);

  const handleRevoke = useCallback(
    async (bearerId: string, label: string) => {
      if (!canMutate) return;
      if (
        !window.confirm(
          `Revoke bearer "${label || bearerId.slice(0, 8)}"? Active integrations using this token will start getting 401 within ~60 seconds.`,
        )
      )
        return;
      setRevoking((prev) => new Set(prev).add(bearerId));
      setError(null);
      try {
        await revokeAgentBearer(orgId, agentId, bearerId);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to revoke bearer');
      } finally {
        setRevoking((prev) => {
          const next = new Set(prev);
          next.delete(bearerId);
          return next;
        });
      }
    },
    [canMutate, orgId, agentId, refresh],
  );

  const activeBearers = useMemo(
    () => bearers.filter((b) => !b.revoked_at),
    [bearers],
  );

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <Label className='text-xs text-muted-foreground'>
          Bearer tokens ({activeBearers.length} active)
        </Label>
      </div>

      {revealedBearer && (
        <div className='font-mono text-xs break-all bg-emerald-500/10 border border-emerald-500/30 rounded p-2 select-all'>
          {revealedBearer}
          <p className='text-[10px] text-emerald-700 mt-1 font-sans'>
            {justCopied
              ? `Copied to clipboard. New bearer "${revealedLabel || 'unlabelled'}" created. Store it now — the plaintext token will never be shown again.`
              : 'Copy this NOW. The plaintext token will not be shown again. Future reads only return the last 8 characters as a display prefix.'}
          </p>
          <div className='mt-2 flex gap-2'>
            <Button
              size='sm'
              variant={justCopied ? 'default' : 'outline'}
              onClick={() => copyToClipboard(revealedBearer)}
            >
              {justCopied ? (
                <>
                  <Check className='h-3 w-3 mr-1' />
                  Copied
                </>
              ) : (
                <>
                  <Copy className='h-3 w-3 mr-1' />
                  Copy bearer
                </>
              )}
            </Button>
            <Button
              size='sm'
              variant='ghost'
              onClick={() => setRevealedBearer(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className='text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded p-2'>
          {error}
        </div>
      )}

      {loading ? (
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <Loader2 className='h-3 w-3 animate-spin' />
          Loading bearers…
        </div>
      ) : bearers.length === 0 ? (
        <div className='text-xs text-muted-foreground italic'>
          No bearers issued yet. Create one to start calling this agent over
          A2A.
        </div>
      ) : (
        <div className='border rounded'>
          <table className='w-full text-xs'>
            <thead>
              <tr className='border-b text-muted-foreground'>
                <th className='text-left px-2 py-1 font-medium'>Prefix</th>
                <th className='text-left px-2 py-1 font-medium'>Label</th>
                <th className='text-left px-2 py-1 font-medium'>Created</th>
                <th className='text-left px-2 py-1 font-medium'>Last used</th>
                <th className='text-right px-2 py-1 font-medium'></th>
              </tr>
            </thead>
            <tbody>
              {bearers.map((b) => {
                const isRevoked = Boolean(b.revoked_at);
                const isInFlight = revoking.has(b.bearer_id);
                return (
                  <tr
                    key={b.bearer_id}
                    className={
                      isRevoked
                        ? 'border-b last:border-b-0 opacity-50'
                        : 'border-b last:border-b-0'
                    }
                  >
                    <td className='px-2 py-1 font-mono'>
                      cap_a2a_••{b.a2a_bearer_token_prefix}
                    </td>
                    <td className='px-2 py-1'>
                      {b.label || (
                        <span className='text-muted-foreground italic'>
                          (none)
                        </span>
                      )}
                      {isRevoked && (
                        <span className='ml-2 text-[10px] uppercase text-destructive'>
                          revoked
                        </span>
                      )}
                    </td>
                    <td className='px-2 py-1 text-muted-foreground'>
                      {relTime(b.created_at)}
                    </td>
                    <td className='px-2 py-1 text-muted-foreground'>
                      {relTime(b.last_used_at)}
                    </td>
                    <td className='px-2 py-1 text-right'>
                      {!isRevoked && canMutate && (
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => handleRevoke(b.bearer_id, b.label)}
                          disabled={isInFlight}
                          title='Revoke this bearer'
                        >
                          {isInFlight ? (
                            <Loader2 className='h-3 w-3 animate-spin' />
                          ) : (
                            <Trash2 className='h-3 w-3' />
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canMutate && (
        <div className='flex items-center gap-2'>
          <Input
            placeholder='Label (e.g. scale-prod, ariq-laptop)'
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className='h-8 text-xs'
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newLabel.trim()) {
                e.preventDefault();
                handleCreate();
              }
            }}
            maxLength={120}
          />
          <Button
            size='sm'
            onClick={handleCreate}
            disabled={creating || !newLabel.trim()}
          >
            {creating ? (
              <Loader2 className='h-3 w-3 mr-1 animate-spin' />
            ) : (
              <Plus className='h-3 w-3 mr-1' />
            )}
            New bearer
          </Button>
        </div>
      )}
      {canMutate && !newLabel.trim() && !creating && (
        <p className='text-[10px] text-muted-foreground'>
          Enter a label to enable — each consumer gets its own named bearer so
          it can be revoked independently.
        </p>
      )}
      {canMutate && (
        <p className='text-[10px] text-muted-foreground'>
          Each bearer is independent — revoking one doesn't affect the others.
          Give each consumer (Scale, EY, a CI runner) its own labelled bearer so
          blast radius stays small.
        </p>
      )}
    </div>
  );
};
